/**
 * Menu Void Rise Animation — periodic void rise on the title screen,
 * using the same smoothstep easing and VoidRenderer choreography as gameplay.
 */

import { VOID, WORLD } from '../../constants';
import type { GameState } from '../../types';
import type { MenuRiseAnim, MenuAnimDeps } from './types';

export class MenuVoidRiseAnimation {
    anim: MenuRiseAnim;

    constructor() {
        const baseVoidY = WORLD.VIRTUAL_HEIGHT - VOID.INITIAL_Y_OFFSET;
        this.anim = {
            phase: 'idle',
            baseVoidY,
            riseStartY: baseVoidY,
            targetVoidY: baseVoidY,
            idleTimer: 3 + Math.random() * 4,
            riseElapsed: 0,
            resetTimer: 0,
            totalRisen: 0,
        };
    }

    update(dt: number, state: GameState, deps: MenuAnimDeps): void {
        const anim = this.anim;

        switch (anim.phase) {
            case 'idle': {
                anim.idleTimer -= dt;
                if (anim.idleTimer <= 0) {
                    anim.riseStartY = state.voidY;
                    anim.targetVoidY = anim.riseStartY - VOID.RISE_PER_ROUND;
                    anim.riseElapsed = 0;
                    anim.totalRisen += VOID.RISE_PER_ROUND;
                    anim.phase = 'rising';

                    deps.voidRenderer.triggerRiseEffects(
                        state.voidY,
                        deps.gameParticles,
                        deps.effects,
                        deps.camera,
                        deps.screenShake,
                    );
                }
                break;
            }

            case 'rising': {
                anim.riseElapsed += dt;

                if (anim.riseElapsed >= VOID.RISE_ANTICIPATION && state.voidY > anim.targetVoidY) {
                    const rawT = Math.min(1, (anim.riseElapsed - VOID.RISE_ANTICIPATION) / VOID.RISE_MOVE_DURATION);
                    const t = rawT * rawT * (3 - 2 * rawT); // smoothstep
                    state.voidY = anim.riseStartY + (anim.targetVoidY - anim.riseStartY) * t;
                }

                const totalDuration = VOID.RISE_ANTICIPATION + VOID.RISE_MOVE_DURATION + VOID.RISE_SETTLE;
                if (anim.riseElapsed >= totalDuration) {
                    state.voidY = anim.targetVoidY;
                    anim.phase = 'settling';

                    const maxRise = VOID.RISE_PER_ROUND * (4 + Math.floor(Math.random() * 5));
                    if (anim.totalRisen >= maxRise) {
                        anim.resetTimer = 0;
                        anim.phase = 'resetting';
                    } else {
                        anim.idleTimer = 10 + Math.random() * 10;
                        anim.phase = 'idle';
                    }
                }
                break;
            }

            case 'resetting': {
                anim.resetTimer += dt;
                const resetDuration = 2.5;
                const t = Math.min(1, anim.resetTimer / resetDuration);
                const eased = 1 - Math.pow(1 - t, 3);
                state.voidY = (anim.baseVoidY - anim.totalRisen) + anim.totalRisen * eased;

                if (t >= 1) {
                    state.voidY = anim.baseVoidY;
                    anim.totalRisen = 0;
                    anim.idleTimer = 8 + Math.random() * 8;
                    anim.phase = 'idle';
                }
                break;
            }
        }
    }

    /** Reset to initial state (when leaving menu). */
    reset(): void {
        this.anim.phase = 'idle';
        this.anim.baseVoidY = WORLD.VIRTUAL_HEIGHT - VOID.INITIAL_Y_OFFSET;
        this.anim.totalRisen = 0;
        this.anim.idleTimer = 3 + Math.random() * 4;
    }
}
